import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Link2, Loader2, Package, Pencil, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import type { Product, ProductInput } from '@fenix/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/EmptyState';
import { DataTablePagination } from '@/components/DataTablePagination';
import { productsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { useCan } from '@/features/auth/store';

type Draft = ProductInput & { id?: string };
const EMPTY: Draft = {
  codename: '',
  name: '',
  sourceSlug: null,
  checkoutUrl: null,
  price: null,
  currency: 'USD',
  offerText: null,
  couponCode: null,
  description: null,
  active: true,
};

const ALL = '__all__';
/** Mesmo tamanho de página das outras listas do painel. */
const PAGE_SIZE = 25;
type ProductRow = Product & { leadCount: number };

export function ProductsPage() {
  const qc = useQueryClient();
  const canEdit = useCan('products.manage');
  const q = useQuery({ queryKey: ['products', 'all'], queryFn: () => productsApi.list(true) });
  const [draft, setDraft] = useState<Draft | null>(null);

  // Filtros e paginação em memória: a lista inteira já vem da API (são dezenas de produtos).
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [source, setSource] = useState(ALL);
  const [link, setLink] = useState<'all' | 'with' | 'without'>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const all = useMemo(() => q.data ?? [], [q.data]);
  const sources = useMemo(
    () => [...new Set(all.map((p) => p.sourceSlug).filter((s): s is string => !!s))].sort(),
    [all],
  );
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return all.filter(
      (p) =>
        (!term || p.codename.toLowerCase().includes(term) || p.name.toLowerCase().includes(term)) &&
        (status === 'all' || (status === 'active') === p.active) &&
        (source === ALL || p.sourceSlug === source) &&
        (link === 'all' || (link === 'with') === !!p.checkoutUrl),
    );
  }, [all, search, status, source, link]);
  const hasFilters = !!search || status !== 'all' || source !== ALL || link !== 'all';

  // Paginação sobre a lista filtrada. Mudar qualquer filtro volta para a primeira página, e a
  // página é limitada ao total para nunca ficar vazia depois de uma exclusão ou ativação.
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [search, status, source, link]);
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const paged = useMemo(
    () => visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [visible, currentPage],
  );
  const clearFilters = () => {
    setSearch('');
    setStatus('all');
    setSource(ALL);
    setLink('all');
  };

  const activeCount = all.filter((p) => p.active).length;
  const missingLink = all.filter((p) => p.active && !p.checkoutUrl).length;
  const missingCoupon = all.filter((p) => p.active && !p.couponCode).length;
  const missingOffer = all.filter((p) => p.active && !p.offerText).length;
  const selectedVisible = visible.filter((p) => selected.has(p.id));
  const allVisibleSelected = visible.length > 0 && selectedVisible.length === visible.length;
  const toggleAll = (on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const p of visible) {
        if (on) next.add(p.id);
        else next.delete(p.id);
      }
      return next;
    });
  const toggleOne = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const bulk = useMutation({
    mutationFn: (active: boolean) => productsApi.bulkActive([...selected], active),
    onSuccess: ({ updated }, active) => {
      toast.success(
        updated
          ? `${updated} produto(s) ${active ? 'ativado(s)' : 'desativado(s)'}`
          : `Nenhum produto mudou — já estavam ${active ? 'ativos' : 'inativos'}`,
      );
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao atualizar'),
  });

  const save = useMutation({
    mutationFn: (d: Draft) => {
      const { id, ...body } = d;
      const clean = {
        ...body,
        checkoutUrl: body.checkoutUrl || null,
        sourceSlug: body.sourceSlug || null,
        offerText: body.offerText || null,
        couponCode: body.couponCode?.trim().toUpperCase() || null,
        description: body.description || null,
        price: body.price === null || Number.isNaN(body.price) ? null : body.price,
      };
      return id ? productsApi.update(id, clean) : productsApi.create(clean);
    },
    onSuccess: () => {
      toast.success('Produto salvo');
      void qc.invalidateQueries({ queryKey: ['products'] });
      setDraft(null);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao salvar'),
  });

  const edit = (p: Product) =>
    setDraft({
      id: p.id,
      codename: p.codename,
      name: p.name,
      sourceSlug: p.sourceSlug,
      checkoutUrl: p.checkoutUrl,
      price: p.price,
      currency: p.currency,
      offerText: p.offerText,
      couponCode: p.couponCode,
      description: p.description,
      active: p.active,
    });

  return (
    <div>
      <PageHeader
        title="Produtos"
        description="Nome amigável, oferta e link de checkout por codename do funil — é o que a IA usa na conversa"
        actions={
          canEdit && (
            <Button onClick={() => setDraft(EMPTY)}>
              <Plus /> Novo produto
            </Button>
          )
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          className="w-56"
          placeholder="Codename ou nome"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Origem" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todas as origens</SelectItem>
            {sources.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={link} onValueChange={(v) => setLink(v as typeof link)}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Com e sem link</SelectItem>
            <SelectItem value="with">Com link de checkout</SelectItem>
            <SelectItem value="without">Sem link de checkout</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button size="sm" variant="ghost" onClick={clearFilters}>
            <X /> Limpar
          </Button>
        )}
        <span className="ml-auto text-sm text-muted-foreground">
          {activeCount} ativo(s) de {all.length}
          {missingLink > 0 && (
            <>
              {' · '}
              <span className="text-warning inline-flex items-center gap-1">
                <AlertTriangle className="size-3.5" /> {missingLink} ativo(s) sem link
              </span>
            </>
          )}
          {missingCoupon > 0 && (
            <>
              {' · '}
              <span className="text-warning inline-flex items-center gap-1">
                <AlertTriangle className="size-3.5" /> {missingCoupon} ativo(s) sem cupom
              </span>
            </>
          )}
          {missingOffer > 0 && (
            <>
              {' · '}
              <span className="text-warning inline-flex items-center gap-1">
                <AlertTriangle className="size-3.5" /> {missingOffer} ativo(s) sem oferta
              </span>
            </>
          )}
        </span>
      </div>

      {canEdit && selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
          <span className="font-medium">{selected.size} selecionado(s)</span>
          <Button size="sm" onClick={() => bulk.mutate(true)} disabled={bulk.isPending}>
            {bulk.isPending && <Loader2 className="animate-spin" />} Ativar
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => bulk.mutate(false)}
            disabled={bulk.isPending}
          >
            Desativar
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Limpar seleção
          </Button>
          <span className="text-xs text-muted-foreground">
            Produto inativo fica fora das campanhas; o "Ligar agora" continua funcionando para
            testes.
          </span>
        </div>
      )}

      <div className="rounded-lg border bg-card">
        {q.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : visible.length ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  {canEdit && (
                    <TableHead className="w-10">
                      <Checkbox
                        aria-label="Selecionar todos os produtos filtrados"
                        checked={
                          allVisibleSelected
                            ? true
                            : selectedVisible.length
                              ? 'indeterminate'
                              : false
                        }
                        onCheckedChange={(v) => toggleAll(v === true)}
                      />
                    </TableHead>
                  )}
                  <TableHead>Codename</TableHead>
                  <TableHead>Nome falado</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Preço</TableHead>
                  <TableHead>Oferta</TableHead>
                  <TableHead>Cupom</TableHead>
                  <TableHead className="text-center">Link</TableHead>
                  <TableHead className="text-center">Leads</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.map((p: ProductRow) => (
                  <TableRow key={p.id} data-state={selected.has(p.id) ? 'selected' : undefined}>
                    {canEdit && (
                      <TableCell>
                        <Checkbox
                          aria-label={`Selecionar ${p.codename}`}
                          checked={selected.has(p.id)}
                          onCheckedChange={(v) => toggleOne(p.id, v === true)}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-mono text-xs">{p.codename}</TableCell>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-sm">{p.sourceSlug ?? '—'}</TableCell>
                    <TableCell className="tabular-nums">
                      {p.price != null ? `${p.currency} ${p.price.toFixed(2)}` : '—'}
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                      {p.offerText ?? (
                        <span
                          className={p.active ? 'text-warning' : undefined}
                          title={
                            p.active
                              ? 'Sem oferta — o piloto e as campanhas não ligam para este produto'
                              : undefined
                          }
                        >
                          —
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {p.couponCode ?? (
                        <span
                          className={p.active ? 'text-warning' : 'text-muted-foreground'}
                          title={
                            p.active
                              ? 'Sem cupom — o piloto e as campanhas não ligam para este produto'
                              : undefined
                          }
                        >
                          —
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {p.checkoutUrl ? (
                        <a
                          href={p.checkoutUrl}
                          target="_blank"
                          rel="noreferrer"
                          title={p.checkoutUrl}
                          className="inline-flex text-success"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Link2 className="size-4" />
                        </a>
                      ) : (
                        <span
                          className="inline-flex text-warning"
                          title="Sem link de checkout — o SMS com o link não sai para este produto"
                        >
                          <AlertTriangle className="size-4" />
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center tabular-nums">{p.leadCount}</TableCell>
                    <TableCell>
                      {p.active ? (
                        <Badge
                          variant="outline"
                          className="border-transparent bg-success/15 font-medium text-success"
                        >
                          <span className="size-1.5 rounded-full bg-success" aria-hidden />
                          Ativo
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="border-transparent bg-warning/20 font-medium text-warning"
                        >
                          <span className="size-1.5 rounded-full bg-warning" aria-hidden />
                          Inativo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {canEdit && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="size-8"
                          onClick={() => edit(p)}
                        >
                          <Pencil />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <DataTablePagination
              page={currentPage}
              pageSize={PAGE_SIZE}
              total={visible.length}
              onPageChange={setPage}
            />
          </>
        ) : (
          <div className="p-6">
            <EmptyState
              icon={Package}
              title={hasFilters ? 'Nenhum produto com esses filtros' : 'Nenhum produto'}
              description={
                hasFilters
                  ? 'Limpe os filtros para ver todos.'
                  : 'Produtos são criados automaticamente ao importar leads; edite-os para dar um nome amigável e oferta.'
              }
            />
          </div>
        )}
      </div>

      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{draft?.id ? 'Editar produto' : 'Novo produto'}</DialogTitle>
            <DialogDescription>
              O "nome falado" e a "oferta" entram nas variáveis {'{{product_name}}'} e {'{{offer}}'}{' '}
              do script.
            </DialogDescription>
          </DialogHeader>
          {draft && (
            <form
              className="grid gap-4 sm:grid-cols-2"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate(draft);
              }}
            >
              <div className="space-y-1.5">
                <Label>Codename *</Label>
                <Input
                  value={draft.codename}
                  onChange={(e) => setDraft({ ...draft, codename: e.target.value })}
                  disabled={!!draft.id}
                  placeholder="hrsw6_fun_b3"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Nome falado *</Label>
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="HorseWood (6 frascos)"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Origem (source_slug)</Label>
                <Input
                  value={draft.sourceSlug ?? ''}
                  onChange={(e) => setDraft({ ...draft, sourceSlug: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-[1fr_80px] gap-2">
                <div className="space-y-1.5">
                  <Label>Preço</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={draft.price ?? ''}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        price: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Moeda</Label>
                  <Input
                    value={draft.currency ?? 'USD'}
                    maxLength={3}
                    onChange={(e) => setDraft({ ...draft, currency: e.target.value.toUpperCase() })}
                  />
                </div>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Link de checkout</Label>
                <Input
                  type="url"
                  value={draft.checkoutUrl ?? ''}
                  onChange={(e) => setDraft({ ...draft, checkoutUrl: e.target.value })}
                  placeholder="https://…"
                />
                <p className="text-xs text-muted-foreground">
                  Sem link o SMS não sai. Um placeholder (ex.: http://localhost) é enviado ao
                  cliente como está.
                </p>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Oferta (o que a IA propõe)</Label>
                <Textarea
                  rows={2}
                  value={draft.offerText ?? ''}
                  onChange={(e) => setDraft({ ...draft, offerText: e.target.value })}
                  placeholder="15% off and free shipping if you complete your order today"
                />
                <p className="text-xs text-muted-foreground">
                  O que a IA fala na ligação. O desconto real é o do cupom: escreva a oferta junto
                  com ele. Produto novo nasce sem oferta.
                </p>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Cupom de desconto (BuyGoods)</Label>
                <Input
                  value={draft.couponCode ?? ''}
                  onChange={(e) => setDraft({ ...draft, couponCode: e.target.value })}
                  placeholder="HONEY15"
                  className="font-mono uppercase"
                />
                <p className="text-xs text-muted-foreground">
                  Código criado no produto na BuyGoods, que aplica a condição no mesmo link de
                  checkout. A IA avisa que o código vai por SMS, e o SMS leva link e cupom juntos.
                  Sem oferta, link ou cupom, o piloto e as campanhas não ligam para este produto.
                </p>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Descrição — só se esta variação for diferente</Label>
                <Textarea
                  rows={4}
                  className="max-h-48 overflow-y-auto"
                  value={draft.description ?? ''}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="Deixe vazio para usar a descrição da marca."
                />
                <p className="text-xs text-muted-foreground">
                  Vazio, a ligação usa a descrição cadastrada em <strong>Marcas</strong> — que é
                  onde normalmente se escreve. Preencha aqui só quando esta variação tiver algo
                  próprio a dizer: o texto do produto substitui o da marca por completo, não soma.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={draft.active ?? true}
                  onCheckedChange={(v) => setDraft({ ...draft, active: v })}
                />
                <Label>Ativo (liberado para campanhas)</Label>
              </div>
              <DialogFooter className="sm:col-span-2">
                <Button type="button" variant="outline" onClick={() => setDraft(null)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={save.isPending || !draft.codename || !draft.name}>
                  {save.isPending && <Loader2 className="animate-spin" />} Salvar
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
